<script setup>
import BreadCrumb from '~/components/common/BreadCrumb'
import {toMoney} from '~/utils/toMoney'
import {useProductApi} from '~/composables/api/useProductApi'
import {useProductCategoryApi} from '~/composables/api/useProductCategoryApi'
import {getPageTitle} from '~/utils/seoUtils'
import Galleria from 'primevue/galleria'
import {useCartStore} from '~/store/cart'
import InlineProductVariantSelector from '~/components/partials/subelements/InlineProductVariantSelector.vue'
import {getProductDefaultImage} from '~/utils/productUtils'
import ProductCustomFields from '~/components/partials/ProductCustomFields.vue'

const productApi = useProductApi()
const productCategoryApi = useProductCategoryApi()
const notification = useNotification()
const cartStore = useCartStore()
const config = useRuntimeConfig()
const selectedVariant = ref(null)
const addItemQty = ref(1)
const imageResponsiveOptions = ref([
  {
    breakpoint: '1300px',
    numVisible: 4
  },
  {
    breakpoint: '575px',
    numVisible: 1
  }
])

const route = useRoute()
const productCategorySlug = route.params.categorySlug
const productSlug = route.params.productSlug

const {data, error} = await useAsyncData(`product-details-${productSlug}`, async () => {
  try {
    const [product, category] = await Promise.all([productApi.getBySlug(productSlug), productCategoryApi.getBySlug(productCategorySlug)])
    const productBlocks = product.hasOwnProperty('blocks') ? product.blocks.sort((a, b) => a.position - b.position) : []
    const productVariants = product.hasOwnProperty('variants') ? product.variants.sort((a, b) => a.price - b.price) : []

    return {
      category,
      product,
      productBlocks,
      productVariants
    }
  } catch (e) {
    throw createError({statusCode: 404, message: 'Página no encontrada'})
  }
})

if (!data.value || error.value) {
  throw createError({statusCode: 404, statusMessage: 'page_not_found', fatal: true})
}

const product = computed(() => data.value.product)
const productImages = computed(() => product.value?.images || null)
const hasMultipleImages = computed(() => productImages.value?.length > 1)
const maxNumVisibleImages = computed(() => hasMultipleImages.value ? 4 : null)
const defaultImage = getProductDefaultImage(product.value)
const category = computed(() => data.value.category)

useHead(() => {
  return {
    title: getPageTitle(product.value.title, config.public.pageTitle),
    meta: [
      {name: 'description', content: product.value.description},
      {name: 'keywords', content: product.value.tags?.map(tag => tag.title).join(',')},
      {property: 'og:type', content: 'product'},
      {property: 'og:title', content: getPageTitle(product.value.title, config.public.pageTitle)},
      {property: 'og:description', content: product.value.description},
      {property: 'og:image', content: defaultImage.value?.image || ''},
      {property: 'product:price.amount', content: product.value.price},
      {property: 'product:price.currency', content: 'EUR'},
    ]
  }
})

const breadcrumb = computed(() => [
  {label: config.public.pageTitle, link: '/'},
  {label: category.value?.title, link: `/tienda/${category.value?.slug}`},
  {label: product.value?.title, link: `/tienda/${category.value?.slug}/${product.value?.slug}`}
])

const hasProductVariants = computed(() => product.value?.variants?.length > 0)
const hasMoreThanOneProductVariant = computed(() => hasProductVariants.value && product.value.variants.length > 1)
const hasProductBlocks = computed(() => product.value?.blocks?.length > 0)

const minProductPrice = computed(() => {
  let currentPrice = product.value.price

  if (hasProductVariants.value) {
    product.value.variants.forEach(variant => {
      if (currentPrice > variant.price && variant.price > 0) {
        currentPrice = variant.price
      }
    })
  }

  return currentPrice
})

const productVariantPrices = computed(() => {
  if (!product.value.variants) return false

  return product.value.variants.reduce((acc, variant) => acc + variant.price, 0) > 0
})

const addToCart = () => {
  if (hasProductVariants.value && !selectedVariant.value) {
    return notification.showError('Debes seleccionar una opción')
  }

  const itemToAdd = {
    product: product.value,
    qty: addItemQty.value,
    selected_variant: selectedVariant.value?.id || null
  }

  cartStore.addItem(itemToAdd)
  notification.showSuccess('Producto añadido al carrito')
  selectedVariant.value = null
}
</script>

<template>
  <div class="grid grid-cols-12 detail-product">
    <div class="col-span-12 lg:col-span-5 px-4 xl:px-8 mb-8">
      <h1 class="text-2xl lg:text-4xl font-bold italic underline text-[#363330] mt-5">{{ product.title }}</h1>
      <BreadCrumb class="mb-3" :items="breadcrumb"/>
      <Galleria
        id="product-image-gallery"
        class="w-full mt-5 px-4 xl:px-8"
        :value="productImages"
        :circular="true"
        :showItemNavigators="hasMultipleImages"
        :responsiveOptions="imageResponsiveOptions"
        :numVisible="maxNumVisibleImages"
      >
        <template #item="slotProps">
          <img :src="slotProps.item.image" :alt="slotProps.item.title" style="width: 100%"/>
        </template>
        <template #thumbnail="slotProps">
          <img class="max-w-[60px]" :src="slotProps.item.image" :alt="slotProps.item.title"/>
        </template>
      </Galleria>
    </div>

    <div class="col-span-12 xl:col-span-3 mt-3 lg:mt-8 px-4 xl:px-8 text-center">
      <p v-if="product.sold_out" class="font-semibold">AGOTADO</p>
      <p v-if="product.only_booking && !product.sold_out" class="font-semibold">SOLO RESERVA</p>

      <template v-if="!product.sold_out && !product.only_booking">
        <h3 class="text-[1rem] italic font-bold underline uppercase mb-2">Precio</h3>
        <div v-if="product.price_discount_percent > 0" class="flex justify-center">
          <p class="text-md italic justify-end line-through mr-3">{{ toMoney(product.price_original) }}</p>
          <p class="text-md italic justify-end ml-3 text-red-800">-{{ product.price_discount_percent }}%</p>
        </div>
        <div class="text-5xl font-bold italic text-red-800">
          <span v-if="productVariantPrices" class="mr-2">desde</span>
          {{ toMoney(minProductPrice) }}
        </div>

        <InlineProductVariantSelector
          v-if="hasProductVariants && hasMoreThanOneProductVariant"
          title="Selecciona una Opción"
          class="mt-8"
          :options="data.productVariants"
          v-model="selectedVariant"
        />

        <div class="text-lg font-bold italic my-8">
          <InputNumber v-model="addItemQty" inputId="minmax-buttons" mode="decimal" showButtons :min="1" :max="100"
                       fluid/>
        </div>
      </template>

      <div v-if="!product.sold_out" class="mt-12 mb-5">
        <button
          class="text-white text-xl italic bg-red-800 w-full py-2 uppercase font-bold"
          @click="addToCart"
        >
          Añadir a la Cesta
        </button>
      </div>
    </div>

    <div class="col-span-12 xl:col-span-4 mt-8 px-4 xl:px-8 text-justify">

      <ProductCustomFields :custom-fields="product.field_values" :video="product?.video"/>

      <div v-if="hasProductBlocks" class="mb-4">
        <h3 class="text-[1rem] italic font-bold mb-2">Temario</h3>
        <ul class="text-[0.9rem] italic">
          <li v-for="block in data.productBlocks" :key="block.id">{{ block.title }}</li>
        </ul>
      </div>
    </div>
  </div>

</template>
